export type ComponentType =
  | 'order'
  | 'product'
  | 'ingredient'
  | 'customer'
  | 'supplier'
  | 'employee';

/** Socket wire types for thành phần (mock component-input). */
export type SocketAttachmentType =
  | 'order'
  | 'product'
  | 'material'
  | 'customer'
  | 'supplier'
  | 'staff';

  
export interface ChatComponentItem {
  type: ComponentType;
  id: string;
  displayName: string;
  subtitle?: string;
  avatarUrl?: string;
  raw: Record<string, unknown>;
}

export interface ChatComponentAttachment {
  type: ComponentType;
  id: string;
  displayName: string;
  data: Record<string, unknown>;
}

/** Payload socket attachments — chỉ { type, data }. */
export interface SocketComponentAttachment {
  type: SocketAttachmentType;
  data: Record<string, unknown>;
}

export interface ChatComponentListPage {
  items: ChatComponentItem[];
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export const COMPONENT_PERMISSION_KEYS: Record<ComponentType, string> = {
  order: 'order_orderlist_view',
  product: 'product_productlist_view',
  ingredient: 'ingredient_list_view',
  customer: 'customer_customerlist_view',
  supplier: 'customer_customerlist_view',
  employee: 'staff_all_view',
};

export const COMPONENT_TYPE_ORDER: ComponentType[] = [
  'order',
  'product',
  'ingredient',
  'customer',
  'supplier',
  'employee',
];

/**
 * Socket wire contract for thành phần:
 * - UI types stay ingredient/employee; wire uses material/staff.
 * - BE + wire keys are snake_case only — never camelCase here.
 * - Never send item.raw as-is; always pick the allowlisted fields below.
 */
const COMPONENT_TO_SOCKET_TYPE: Record<ComponentType, SocketAttachmentType> = {
  order: 'order',
  product: 'product',
  ingredient: 'material',
  customer: 'customer',
  supplier: 'supplier',
  employee: 'staff',
};

/** Copy key only if present (keeps null / [] — useful for scheduled_at, roles). */
const pick = (raw: Record<string, unknown>, key: string): unknown =>
  raw[key] !== undefined ? raw[key] : undefined;

/** Allowlist top-level snake_case keys for the socket payload. */
const pickKeys = (
  raw: Record<string, unknown>,
  keys: string[],
): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  keys.forEach((key) => {
    const value = pick(raw, key);
    if (value !== undefined) out[key] = value;
  });
  return out;
};

const mapArray = <T>(
  value: unknown,
  mapper: (item: Record<string, unknown>) => T,
): T[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  return value
    .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
    .map(mapper);
};

/** product → attachments[].data */
const mapWholesalePrices = (value: unknown): Record<string, unknown>[] | undefined =>
  mapArray(value, (wp) => pickKeys(wp, ['min_quantity', 'price']));

const mapVariantUnits = (value: unknown): Record<string, unknown>[] | undefined =>
  mapArray(value, (unit) => {
    const mapped = pickKeys(unit, [
      'name',
      'quantity',
      'price',
      'retail_price',
      'unit_cost',
    ]);
    const wholesalePrices = mapWholesalePrices(pick(unit, 'wholesale_prices'));
    if (wholesalePrices) mapped.wholesale_prices = wholesalePrices;
    return mapped;
  });

const buildProductData = (raw: Record<string, unknown>): Record<string, unknown> => {
  const data = pickKeys(raw, [
    'id',
    'name',
    'product_code',
    'image_urls',
    'description',
    'total_sold',
    'unit_name',
    'stock_mode',
    'min_price',
    'max_price',
    'retail_price',
    'base_price',
    'promotion_price',
    'variant_count',
    'has_hourly_rate',
    'tax_rate',
    'tax_discount_rate',
    'pit_rate',
  ]);

  const categories = mapArray(pick(raw, 'categories'), (item) =>
    pickKeys(item, ['name']),
  );
  if (categories) data.categories = categories;

  const variants = mapArray(pick(raw, 'variants'), (item) => {
    const variant = pickKeys(item, [
      'retail_price',
      'base_price',
      'unit_cost',
      'wholesale_price',
      'variant_type',
      'variant_name',
    ]);
    const attributes = mapArray(pick(item, 'attributes'), (attr) =>
      pickKeys(attr, ['name']),
    );
    if (attributes) variant.attributes = attributes;
    const variantWholesalePrices = mapWholesalePrices(pick(item, 'wholesale_prices'));
    if (variantWholesalePrices) variant.wholesale_prices = variantWholesalePrices;
    const nestedVariantUnits = mapVariantUnits(pick(item, 'variant_units'));
    if (nestedVariantUnits) variant.variant_units = nestedVariantUnits;
    return variant;
  });
  if (variants) data.variants = variants;

  const variantAttributes = mapArray(pick(raw, 'variant_attributes'), (item) => {
    const group = pickKeys(item, ['name']);
    const attributes = mapArray(pick(item, 'attributes'), (attr) =>
      pickKeys(attr, ['name']),
    );
    if (attributes) group.attributes = attributes;
    return group;
  });
  if (variantAttributes) data.variant_attributes = variantAttributes;

  const variantUnits = mapVariantUnits(pick(raw, 'variant_units'));
  if (variantUnits) data.variant_units = variantUnits;

  const modifierGroups = mapArray(pick(raw, 'modifier_groups'), (item) => {
    const group = pickKeys(item, [
      'name',
      'is_required',
      'allow_multiple_selections',
      'allow_multiple_quantities',
    ]);
    const modifiers = mapArray(pick(item, 'modifiers'), (mod) =>
      pickKeys(mod, ['name', 'additional_price', 'unit_cost']),
    );
    if (modifiers) group.modifiers = modifiers;
    return group;
  });
  if (modifierGroups) data.modifier_groups = modifierGroups;

  return data;
};

/** order → attachments[].data */
const buildOrderData = (raw: Record<string, unknown>): Record<string, unknown> => {
  const data = pickKeys(raw, [
    'id',
    'order_number',
    'coupon_code',
    'subtotal_amount',
    'promotion_discount_amount',
    'shipping_fee',
    'total_amount',
    'order_status',
    'payment_method',
    'order_note',
    'delivery_method',
    'manual_discount_amount',
    'balance_due',
    'paid_amount',
    'order_updated_at',
    'loyalty_discount_amount',
    'shipping_method',
    'delivery_status',
    'surcharge_amount',
    'refund_fee_amount',
    'additional_refund_amount',
    'e_invoice_status',
    'refund_status',
    'remaining_customer_point',
  ]);

  const buyer = pick(raw, 'buyer_snapshot');
  if (buyer && typeof buyer === 'object') {
    data.buyer_snapshot = pickKeys(buyer as Record<string, unknown>, [
      'phone_number',
      'name',
      'address',
    ]);
  }

  const staff = pick(raw, 'staff_snapshot');
  if (staff && typeof staff === 'object') {
    data.staff_snapshot = pickKeys(staff as Record<string, unknown>, [
      'staff_name',
      'phone_number',
    ]);
  }

  const price = pick(raw, 'price_snapshot');
  if (price && typeof price === 'object') {
    data.price_snapshot = pickKeys(price as Record<string, unknown>, [
      'total_of_items_initial',
      'total_amount_before_tax',
      'total_amount_after_tax',
    ]);
  }

  const lines = mapArray(pick(raw, 'lines'), (line) => {
    const mapped = pickKeys(line, [
      'item_name',
      'price_before_discount',
      'line_total_amount',
      'variant_name',
      'unit_name',
    ]);
    const addOns = mapArray(pick(line, 'add_ons'), (addOn) =>
      pickKeys(addOn, ['modifier_name', 'modifier_quantity']),
    );
    if (addOns) mapped.add_ons = addOns;
    return mapped;
  });
  if (lines) data.lines = lines;

  return data;
};

/** customer | supplier → attachments[].data (same contact shape) */
const buildContactData = (raw: Record<string, unknown>): Record<string, unknown> => {
  const data = pickKeys(raw, [
    'id',
    'contact_name',
    'contact_email',
    'contact_phone',
    'contact_gender',
    'birth_date',
    'contact_role',
    'full_address',
    'loyalty_points',
    'total_quantity_customer',
    'total_amount_customer',
    'total_quantity_supplier',
    'total_amount_supplier',
  ]);

  const tags = mapArray(pick(raw, 'contact_tag'), (item) =>
    pickKeys(item, ['id', 'tag_name', 'tag_color', 'business_id', 'tag_type', 'role']),
  );
  if (tags) data.contact_tag = tags;

  const groups = mapArray(pick(raw, 'contact_groups'), (item) =>
    pickKeys(item, ['id', 'name', 'code', 'business_id', 'contact_id']),
  );
  if (groups) data.contact_groups = groups;

  const lastOrder = pick(raw, 'last_order');
  if (lastOrder && typeof lastOrder === 'object') {
    data.last_order = pickKeys(lastOrder as Record<string, unknown>, [
      'id',
      'order_number',
      'created_at',
    ]);
  }

  const billing = pick(raw, 'billing_info');
  if (billing && typeof billing === 'object') {
    data.billing_info = pickKeys(billing as Record<string, unknown>, [
      'billing_contact_name',
      'billing_email',
      'billing_phone',
      'complete_billing_address',
      'tax_code',
      'company_name',
    ]);
  }

  return data;
};

/**
 * employee → wire type staff.
 * roles: [] = owner; non-empty = roles[].role.role_name for display.
 */
const buildStaffData = (raw: Record<string, unknown>): Record<string, unknown> => {
  const data = pickKeys(raw, ['id', 'user_id', 'staff_name', 'staff_phone']);

  const rolesRaw = pick(raw, 'roles');
  if (Array.isArray(rolesRaw)) {
    data.roles = rolesRaw
      .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
      .map((item) => {
        const role = (item.role && typeof item.role === 'object'
          ? (item.role as Record<string, unknown>)
          : item);
        return {
          role: pickKeys(role, ['role_name', 'description']),
        };
      });
  }

  return data;
};

/** ingredient → wire type material */
const buildMaterialData = (raw: Record<string, unknown>): Record<string, unknown> => {
  const data = pickKeys(raw, [
    'id',
    'name',
    'material_code',
    'unit_price',
    'on_hand_quantity',
    'low_stock_threshold',
    'stock_value',
  ]);

  const unit = pick(raw, 'unit');
  if (unit && typeof unit === 'object') {
    data.unit = pickKeys(unit as Record<string, unknown>, ['name']);
  } else if (typeof unit === 'string') {
    data.unit = { name: unit };
  }

  return data;
};

/** Route UI component type → trimmed snake_case data for wire. */
const buildAttachmentData = (
  type: ComponentType,
  raw: Record<string, unknown>,
): Record<string, unknown> => {
  switch (type) {
    case 'product':
      return buildProductData(raw);
    case 'order':
      return buildOrderData(raw);
    case 'customer':
    case 'supplier':
      return buildContactData(raw);
    case 'employee':
      return buildStaffData(raw);
    case 'ingredient':
      return buildMaterialData(raw);
    default:
      return {};
  }
};

/**
 * Build socket `attachments` when sending a message with a selected component.
 * Output shape: [{ type, data }] — data.id included, no displayName on the wire.
 * Do not bypass this and attach item.raw.
 */
export const buildComponentAttachments = (
  item: ChatComponentItem,
): SocketComponentAttachment[] => [
  {
    type: COMPONENT_TO_SOCKET_TYPE[item.type],
    data: buildAttachmentData(item.type, item.raw ?? {}),
  },
];

/** Chip label: order always shows "Đơn hàng …" (history may return code only). */
export const formatComponentChipLabel = (
  type: ComponentType | null | undefined,
  displayName: string,
): string => {
  const name = (displayName || '').trim();
  if (!name) return '';
  if (type === 'order') {
    return /^đơn hàng\b/i.test(name) ? name : `Đơn hàng ${name}`;
  }
  return name;
};

export const getComponentChipLabel = (
  item: Pick<ChatComponentItem, 'type' | 'displayName'>,
): string => formatComponentChipLabel(item.type, item.displayName);

const SOCKET_TO_COMPONENT_TYPE: Record<string, ComponentType> = {
  order: 'order',
  product: 'product',
  material: 'ingredient',
  ingredient: 'ingredient',
  customer: 'customer',
  supplier: 'supplier',
  staff: 'employee',
  employee: 'employee',
};

const LABEL_PREFIX_TO_TYPE: Array<{ prefix: string; type: ComponentType }> = [
  { prefix: 'đơn hàng', type: 'order' },
  { prefix: 'sản phẩm', type: 'product' },
  { prefix: 'nguyên liệu', type: 'ingredient' },
  { prefix: 'khách hàng', type: 'customer' },
  { prefix: 'nhà cung cấp', type: 'supplier' },
  { prefix: 'nhân viên', type: 'employee' },
];

/** Resolve UI component type from socket attachmentType or Vietnamese label text. */
export const resolveComponentTypeFromInfo = (
  attachmentType?: string | null,
  text?: string | null,
): ComponentType | null => {
  const raw = (attachmentType || '').trim().toLowerCase();
  if (raw && SOCKET_TO_COMPONENT_TYPE[raw]) {
    return SOCKET_TO_COMPONENT_TYPE[raw];
  }

  const label = (text || '').trim().toLowerCase();
  if (!label) return null;

  for (const entry of LABEL_PREFIX_TO_TYPE) {
    if (label.startsWith(entry.prefix)) return entry.type;
  }
  return null;
};
